import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// feature driver-cards-v2
import * as A from './actions';
import { createResourceGroupingState } from './grouping';
import { getInitialState, getInitialEditSession } from './helpers';
import { isRouteBuilderLoad, getTimelineEditLocks, isTimelineEditAllowed } from './edit-locks';
import {
  createEditDraftStopCommand,
  createEditDraftStopAppointmentsCommand,
} from './draft-stop-edits';
import {
  hydrateEditSessionEvents,
  reconcileEditSessionLoad,
  resolveEditSessionConflict,
} from './components/edit-mode/reconciliation';
import {
  getTimelineRange,
  clampVisibleDays,
  cloneTimelineLoads,
  redoTimelineCommand,
  undoTimelineCommand,
  getDateRangeDayCount,
  areTimelineLoadsEqual,
  commitTimelineCommand,
} from './timeline';
import {
  MIN_VISIBLE_DAYS,
  MAX_VISIBLE_DAYS,
  EDIT_HISTORY_LIMIT,
  EDIT_COMMAND_INSERT_LOAD,
  EDIT_COMMAND_REMOVE_LOAD,
  EDIT_COMMAND_EDIT_LOAD_RATE,
  EDIT_COMMAND_EDIT_STOP_APPOINTMENTS,
} from './constants';
//////////////////////////////////////////////////

const setPageVisited = (state, pageVisited) => P.$set('pageVisited', pageVisited, state);

const loadPageDefaultsRequest = (state) => P.$set('pageDefaultsLoaded', false, state);

const applyPageDefaults = (state, defaults) => ({
  ...getInitialState(),
  pageDefaultsLoaded: true,
  pageVisited: state.pageVisited,
  ...defaults,
});

const setVisibleDays = (state, visibleDays) => P.$set(
  'visibleDays',
  clampVisibleDays(
    visibleDays,
    MIN_VISIBLE_DAYS,
    Math.min(MAX_VISIBLE_DAYS, getDateRangeDayCount(state.requestDateRange)),
  ),
  state,
);

const selectResource = (state, resourceId) => P.$set('selectedResourceId', resourceId, state);
const setShowTeam = (state, showTeam) => P.$set('showTeam', showTeam, state);

const setRequestDateRange = (state, dateRange) => setVisibleDays(
  P.$set('requestDateRange', dateRange, state),
  state.visibleDays,
);

const updateLoadColor = (load, { color, loadId }) => {
  if (load.id !== loadId) return load;

  return {
    ...load,
    source: {
      ...(load.source || {}),
      [GC.FIELD_DRIVER_CARD_COLOR]: color,
    },
    driverCardColor: color,
  };
};

const updateLoadCollectionColor = (loads, payload) => (
  loads.map((load) => updateLoadColor(load, payload))
);

const withoutObjectKey = (object, key) => Object.entries(object || {}).reduce((result, [name, value]) => (
  name === key ? result : { ...result, [name]: value }
), {});

const replaceAssignedLoad = (resources, loadId, replacement) => resources.map((resource) => ({
  ...resource,
  loads: resource.loads.map((load) => (load.id === loadId ? replacement : load)),
}));

const updateLoadColorSuccess = (state, payload) => P.$all(
  P.$set(
    'assignedResources.items',
    state.assignedResources.items.map((resource) => ({
      ...resource,
      loads: updateLoadCollectionColor(resource.loads, payload),
    })),
  ),
  P.$set(
    'unassignedLoads.items',
    updateLoadCollectionColor(state.unassignedLoads.items, payload),
  ),
  P.$set(
    'editSession.draftLoads',
    updateLoadCollectionColor(state.editSession.draftLoads, payload),
  ),
  P.$set(
    'editSession.baselineLoads',
    updateLoadCollectionColor(state.editSession.baselineLoads, payload),
  ),
  state,
);

const createEditSession = (resourceId, loads) => {
  const baselineLoads = cloneTimelineLoads(loads);

  return {
    ...getInitialEditSession(),
    resourceId,
    baselineLoads,
    sessionId: G.generateGuid(),
    draftLoads: cloneTimelineLoads(loads),
  };
};

const openEditSession = (state, { resourceId, loads }) => P.$all(
  P.$set('unassignedPanelOpen', true),
  P.$set('selectedResourceId', resourceId),
  P.$set('editSession', createEditSession(resourceId, loads)),
  state,
);

const assignUnassignedLoad = (state, { load, resourceId, resourceLoads }) => {
  const currentSession = state.editSession.resourceId === resourceId
    ? state.editSession
    : createEditSession(resourceId, resourceLoads);

  if (currentSession.draftLoads.some(({ id }) => id === load.id)) return state;

  const editSession = commitTimelineCommand(currentSession, {
    before: null,
    loadId: load.id,
    kind: EDIT_COMMAND_INSERT_LOAD,
    after: cloneTimelineLoads([load])[0],
  }, EDIT_HISTORY_LIMIT);

  return P.$all(
    P.$set('unassignedPanelOpen', true),
    P.$set('selectedResourceId', resourceId),
    P.$set('editSession', editSession),
    state,
  );
};

const discardEditSession = (state) => P.$set('editSession', getInitialEditSession(), state);

const saveEditSessionRequest = (state) => P.$all(
  P.$set('editSession.saving', true),
  P.$set('editSession.saveError', null),
  state,
);

const saveEditSessionFailure = (state, error) => P.$all(
  P.$set('editSession.saving', false),
  P.$set('editSession.saveError', error),
  state,
);

const routeBuilderRouteSaved = (state, { sessionId, token }) => (
  state.editSession.sessionId === sessionId
    ? P.$set('editSession.routeBuilderSavedToken', token, state)
    : state
);

const cancelUnassignedLoadAssignment = (state, { loadId, resourceId, ratePreparationId }) => {
  const session = state.editSession;
  const matches = (load) => load && load.id === loadId && load.ratePreparationId === ratePreparationId;
  const load = session.draftLoads.find(matches);

  if (session.resourceId !== resourceId || !load || load.rate) return state;

  const draftLoads = session.draftLoads.filter((item) => !matches(item));
  const keepCommand = (command) => !matches(command.before) && !matches(command.after);

  return P.$set('editSession', {
    ...session,
    draftLoads,
    undoStack: session.undoStack.filter(keepCommand),
    redoStack: session.redoStack.filter(keepCommand),
    dirty: !areTimelineLoadsEqual(session.baselineLoads, draftLoads),
  }, state);
};

const removeDraftLoad = (state, { loadId, sessionId }) => {
  const session = state.editSession;
  const load = session.draftLoads.find(({ id }) => id === loadId);
  const originalLoad = session.baselineLoads.some(({ id }) => id === loadId);

  if (session.sessionId !== sessionId || !load || originalLoad) return state;

  const editSession = commitTimelineCommand(session, {
    loadId,
    after: null,
    before: load,
    kind: EDIT_COMMAND_REMOVE_LOAD,
  }, EDIT_HISTORY_LIMIT);

  return P.$set('editSession', {
    ...editSession,
    syncErrors: withoutObjectKey(editSession.syncErrors, loadId),
    syncRequests: withoutObjectKey(editSession.syncRequests, loadId),
    syncConflicts: withoutObjectKey(editSession.syncConflicts, loadId),
    serverRevisionByLoadId: withoutObjectKey(editSession.serverRevisionByLoadId, loadId),
  }, state);
};

const setDraftLoadRate = (state, { rate, loadId, resourceId, ratePreparationId }) => {
  const session = state.editSession;
  const matches = (load) => load && load.id === loadId && load.ratePreparationId === ratePreparationId;

  if (session.resourceId !== resourceId || !session.draftLoads.some(matches)) return state;

  const updateLoad = (load) => (matches(load) ? { ...load, rate } : load);

  const updateCommand = (command) => ({
    ...command,
    after: updateLoad(command.after),
    before: updateLoad(command.before),
  });

  // Rate initialization belongs to the insertion, including snapshots from later timeline gestures.
  return P.$set('editSession', {
    ...session,
    draftLoads: session.draftLoads.map(updateLoad),
    undoStack: session.undoStack.map(updateCommand),
    redoStack: session.redoStack.map(updateCommand),
  }, state);
};

const editDraftLoadRate = (state, { rate, loadId, sessionId, resourceId, ratePreparationId }) => {
  const session = state.editSession;
  const load = session.draftLoads.find(({ id }) => id === loadId);

  if (!load || session.sessionId !== sessionId || session.resourceId !== resourceId) return state;

  if (load.ratePreparationId !== ratePreparationId) return state;

  return P.$set('editSession', commitTimelineCommand(session, {
    loadId,
    before: load,
    after: { ...load, rate },
    kind: EDIT_COMMAND_EDIT_LOAD_RATE,
  }, EDIT_HISTORY_LIMIT), state);
};

const commitTimelineEdit = (state, command) => {
  const load = state.editSession.draftLoads.find(({ id }) => id === command.loadId);
  const ratePreparationId = command.before && command.before.ratePreparationId;

  const appointmentEdit = command.kind === EDIT_COMMAND_EDIT_STOP_APPOINTMENTS
    && load
    && command.after
    && !isRouteBuilderLoad(load)
    && command.after.startAt === load.startAt
    && command.after.endAt === load.endAt
    && load.events.length === command.after.events.length
    && load.events.every((event, index) => (
      event.id === command.after.events[index].id
      && event.at === command.after.events[index].at
    ));

  if (
    state.editSession.syncRequests[command.loadId]
    || state.editSession.syncErrors[command.loadId]
    || state.editSession.syncConflicts[command.loadId]
  ) return state;

  if (!appointmentEdit && !isTimelineEditAllowed(load, command, getTimelineRange(state.requestDateRange))) return state;

  if (ratePreparationId && (!load || load.ratePreparationId !== ratePreparationId)) return state;

  const preserveRate = (snapshot) => (
    snapshot && load && load.rate && snapshot.ratePreparationId === load.ratePreparationId
      ? { ...snapshot, rate: load.rate }
      : snapshot
  );

  const preparedCommand = {
    ...command,
    after: preserveRate(command.after),
    before: preserveRate(command.before),
  };

  return P.$set(
    'editSession',
    commitTimelineCommand(state.editSession, preparedCommand, EDIT_HISTORY_LIMIT),
    state,
  );
};

const commitDraftStopEdit = (state, payload, createCommand) => {
  const session = state.editSession;

  if (session.sessionId !== payload.sessionId) return state;

  const load = session.draftLoads.find(({ id }) => id === payload.loadId);

  if (!load) return state;

  if (createCommand === createEditDraftStopCommand) {
    const locks = getTimelineEditLocks(load, getTimelineRange(state.requestDateRange));
    const eventIndex = load.events.findIndex(({ id }) => id === payload.eventId);

    if (locks.readOnly || eventIndex < 0 || locks.events[eventIndex]) return state;
  }

  const command = createCommand(
    load,
    payload.eventId,
    payload.submittedValues,
    payload.event,
  );

  return command ? commitTimelineEdit(state, command) : state;
};

const editDraftStop = (state, payload) => (
  commitDraftStopEdit(state, payload, createEditDraftStopCommand)
);

const editDraftStopAppointments = (state, payload) => (
  commitDraftStopEdit(state, payload, createEditDraftStopAppointmentsCommand)
);

const undoTimelineEdit = (state) => P.$set(
  'editSession',
  undoTimelineCommand(state.editSession),
  state,
);

const redoTimelineEdit = (state) => P.$set(
  'editSession',
  redoTimelineCommand(state.editSession),
  state,
);

const hydrateEditLoadEvents = (state, { events, loadId, sessionId }) => {
  const session = state.editSession;

  if (session.sessionId !== sessionId || !Array.isArray(events)) return state;

  return P.$set(
    'editSession',
    hydrateEditSessionEvents(session, loadId, events, getTimelineRange(state.requestDateRange)),
    state,
  );
};

const refreshEditLoadStart = (state, { loadId, requestId, sessionId }) => {
  const session = state.editSession;

  if (session.sessionId !== sessionId || !session.draftLoads.some(({ id }) => id === loadId)) return state;

  return P.$set('editSession', {
    ...session,
    syncErrors: withoutObjectKey(session.syncErrors, loadId),
    syncRequests: { ...session.syncRequests, [loadId]: requestId },
  }, state);
};

const refreshEditLoadSuccess = (state, { load, loadId, requestId, sessionId }) => {
  const session = state.editSession;

  if (session.sessionId !== sessionId || !session.draftLoads.some(({ id }) => id === loadId)) return state;

  if (requestId && session.syncRequests[loadId] !== requestId) return state;

  const reconciledSession = reconcileEditSessionLoad(
    session,
    load,
    getTimelineRange(state.requestDateRange),
  );

  const nextSession = {
    ...reconciledSession,
    syncErrors: withoutObjectKey(reconciledSession.syncErrors, loadId),
    syncRequests: withoutObjectKey(reconciledSession.syncRequests, loadId),
  };

  const assignedLoad = nextSession.baselineLoads.find(({ id }) => id === loadId);

  return P.$all(
    P.$set('editSession', nextSession),
    P.$set(
      'assignedResources.items',
      assignedLoad
        ? replaceAssignedLoad(state.assignedResources.items, loadId, assignedLoad)
        : state.assignedResources.items,
    ),
    state,
  );
};

const refreshEditLoadFailure = (state, { error, loadId, requestId, sessionId }) => {
  const session = state.editSession;

  if (session.sessionId !== sessionId || session.syncRequests[loadId] !== requestId) return state;

  return P.$set('editSession', {
    ...session,
    syncErrors: { ...session.syncErrors, [loadId]: error },
    syncRequests: withoutObjectKey(session.syncRequests, loadId),
  }, state);
};

const resolveEditLoadConflict = (state, { loadId, resolution, sessionId }) => {
  if (state.editSession.sessionId !== sessionId) return state;

  return P.$set(
    'editSession',
    resolveEditSessionConflict(state.editSession, loadId, resolution),
    state,
  );
};

const setResourceMode = (state, resourceMode) => P.$all(
  P.$set('resourceMode', resourceMode),
  P.$set('resourceFilter', ENUMS.ENUM_NONE),
  P.$set('resourceGroupBy', ENUMS.ENUM_NONE),
  P.$set('selectedResourceId', null),
  P.$set('editSession', getInitialEditSession()),
  P.$set('resourceGroupInfo.data', null),
  P.$set('resourceGrouping', createResourceGroupingState()),
  P.$set('assignedResources.items', []),
  state,
);

const setResourceFilter = (state, resourceFilter) => (
  state.editSession.resourceId ? state : P.$all(
    P.$set('resourceFilter', resourceFilter),
    P.$set('selectedResourceId', null),
    state,
  )
);

const setResourceGroupBy = (state, resourceGroupBy) => (
  state.editSession.resourceId ? state : P.$all(
    P.$set('resourceGroupBy', resourceGroupBy),
    P.$set('selectedResourceId', null),
    P.$set('resourceGrouping', createResourceGroupingState()),
    P.$set('assignedResources.items', []),
    state,
  )
);

const setResourceSearch = (state, resourceSearch) => P.$set('resourceSearch', resourceSearch, state);

const setCustomerFilter = (state, customerFilter) => (
  state.editSession.resourceId ? state : P.$set('customerFilter', customerFilter || null, state)
);

const setLoadReferenceFilter = (state, loadReferenceFilter) => (
  state.editSession.resourceId ? state : P.$set('loadReferenceFilter', loadReferenceFilter || '', state)
);

const toggleUnassignedPanel = (state) => P.$toggle('unassignedPanelOpen', state);

const setListLoading = (path, state) => P.$all(
  P.$set(`${path}.error`, null),
  P.$set(`${path}.loading`, true),
  state,
);

const setListSuccess = (path, state, items) => P.$all(
  P.$set(`${path}.error`, null),
  P.$set(`${path}.items`, items),
  P.$set(`${path}.loading`, false),
  state,
);

const setListFailure = (path, state, error) => P.$all(
  P.$set(`${path}.error`, error),
  P.$set(`${path}.loading`, false),
  state,
);

const loadAssignedResourcesRequest = (state) => setListLoading('assignedResources', state);

const loadAssignedResourcesSuccess = (state, { grouping, items }) => P.$all(
  P.$set('assignedResources.error', null),
  P.$set('assignedResources.items', items),
  P.$set('assignedResources.loading', false),
  P.$set('resourceGrouping', grouping || createResourceGroupingState()),
  state,
);

const loadAssignedResourcesFailure = (state, error) => setListFailure('assignedResources', state, error);

const loadResourceGroupInfoRequest = (state) => P.$all(
  P.$set(
    'assignedResources.items',
    state.resourceGroupBy === ENUMS.ENUM_NONE ? state.assignedResources.items : [],
  ),
  P.$set('resourceGroupInfo.data', null),
  P.$set('resourceGroupInfo.error', null),
  P.$set('resourceGroupInfo.loading', true),
  P.$set('resourceGrouping', createResourceGroupingState()),
  state,
);

const loadResourceGroupInfoSuccess = (state, data) => P.$all(
  P.$set('resourceGroupInfo.data', data),
  P.$set('resourceGroupInfo.error', null),
  P.$set('resourceGroupInfo.loading', false),
  state,
);

const loadResourceGroupInfoFailure = (state, error) => P.$all(
  P.$set('resourceGroupInfo.error', error),
  P.$set('resourceGroupInfo.loading', false),
  state,
);

const toggleResourceGroup = (state, { groupKey, retry }) => {
  if (retry && state.resourceGrouping.expandedKeys.includes(groupKey)) return state;

  const expandedKeys = state.resourceGrouping.expandedKeys.includes(groupKey)
    ? state.resourceGrouping.expandedKeys.filter((key) => key !== groupKey)
    : [...state.resourceGrouping.expandedKeys, groupKey];

  return P.$set('resourceGrouping.expandedKeys', expandedKeys, state);
};

const loadResourceGroupStart = (state, { groupKey }) => P.$all(
  P.$set(
    'resourceGrouping.errorsByKey',
    withoutObjectKey(state.resourceGrouping.errorsByKey, groupKey),
  ),
  P.$set(
    'resourceGrouping.loadingKeys',
    [...new Set([...state.resourceGrouping.loadingKeys, groupKey])],
  ),
  state,
);

const loadResourceGroupSuccess = (state, { groupKey, resources }) => {
  const nextIds = resources.map(({ id }) => id);
  const nextIdSet = new Set(nextIds);
  const previousIds = state.resourceGrouping.resourceIdsByKey[groupKey] || [];
  const resourcesById = new Map(state.assignedResources.items.map((resource) => [resource.id, resource]));

  const resourceIdsByKey = Object.entries(state.resourceGrouping.resourceIdsByKey).reduce(
    (result, [key, ids]) => ({
      ...result,
      [key]: key === groupKey ? nextIds : ids.filter((id) => !nextIdSet.has(id)),
    }),
    {},
  );

  previousIds.forEach((id) => resourcesById.delete(id));
  resources.forEach((resource) => resourcesById.set(resource.id, resource));

  return P.$all(
    P.$set('assignedResources.items', [...resourcesById.values()]),
    P.$set(
      'resourceGrouping.errorsByKey',
      withoutObjectKey(state.resourceGrouping.errorsByKey, groupKey),
    ),
    P.$set(
      'resourceGrouping.loadedKeys',
      [...new Set([...state.resourceGrouping.loadedKeys, groupKey])],
    ),
    P.$set(
      'resourceGrouping.loadingKeys',
      state.resourceGrouping.loadingKeys.filter((key) => key !== groupKey),
    ),
    P.$set('resourceGrouping.resourceIdsByKey', {
      ...resourceIdsByKey,
      [groupKey]: nextIds,
    }),
    state,
  );
};

const loadResourceGroupFailure = (state, { error, groupKey }) => P.$all(
  P.$set('resourceGrouping.errorsByKey', {
    ...state.resourceGrouping.errorsByKey,
    [groupKey]: error,
  }),
  P.$set(
    'resourceGrouping.loadingKeys',
    state.resourceGrouping.loadingKeys.filter((key) => key !== groupKey),
  ),
  state,
);

const loadUnassignedLoadsRequest = (state) => setListLoading('unassignedLoads', state);
const loadUnassignedLoadsSuccess = (state, items) => setListSuccess('unassignedLoads', state, items);
const loadUnassignedLoadsFailure = (state, error) => setListFailure('unassignedLoads', state, error);

export default createReducer({
  [A.setShowTeam]: setShowTeam,
  [A.editDraftStop]: editDraftStop,
  [A.setPageVisited]: setPageVisited,
  [A.applyPageDefaults]: applyPageDefaults,
  [A.loadPageDefaultsRequest]: loadPageDefaultsRequest,
  [A.setVisibleDays]: setVisibleDays,
  [A.selectResource]: selectResource,
  [A.openEditSession]: openEditSession,
  [A.setResourceMode]: setResourceMode,
  [A.removeDraftLoad]: removeDraftLoad,
  [A.setDraftLoadRate]: setDraftLoadRate,
  [A.undoTimelineEdit]: undoTimelineEdit,
  [A.redoTimelineEdit]: redoTimelineEdit,
  [A.setResourceFilter]: setResourceFilter,
  [A.editDraftLoadRate]: editDraftLoadRate,
  [A.setResourceSearch]: setResourceSearch,
  [A.setCustomerFilter]: setCustomerFilter,
  [A.setResourceGroupBy]: setResourceGroupBy,
  [A.discardEditSession]: discardEditSession,
  [A.commitTimelineEdit]: commitTimelineEdit,
  [A.setRequestDateRange]: setRequestDateRange,
  [A.toggleResourceGroup]: toggleResourceGroup,
  [A.refreshEditLoadStart]: refreshEditLoadStart,
  [A.assignUnassignedLoad]: assignUnassignedLoad,
  [A.hydrateEditLoadEvents]: hydrateEditLoadEvents,
  [A.toggleUnassignedPanel]: toggleUnassignedPanel,
  [A.refreshEditLoadSuccess]: refreshEditLoadSuccess,
  [A.refreshEditLoadFailure]: refreshEditLoadFailure,
  [A.routeBuilderRouteSaved]: routeBuilderRouteSaved,
  [A.setLoadReferenceFilter]: setLoadReferenceFilter,
  [A.saveEditSessionRequest]: saveEditSessionRequest,
  [A.saveEditSessionFailure]: saveEditSessionFailure,
  [A.updateLoadColorSuccess]: updateLoadColorSuccess,
  [A.loadResourceGroupStart]: loadResourceGroupStart,
  [A.resolveEditLoadConflict]: resolveEditLoadConflict,
  [A.loadResourceGroupSuccess]: loadResourceGroupSuccess,
  [A.loadResourceGroupFailure]: loadResourceGroupFailure,
  [A.editDraftStopAppointments]: editDraftStopAppointments,
  [A.loadUnassignedLoadsRequest]: loadUnassignedLoadsRequest,
  [A.loadUnassignedLoadsSuccess]: loadUnassignedLoadsSuccess,
  [A.loadUnassignedLoadsFailure]: loadUnassignedLoadsFailure,
  [A.loadResourceGroupInfoRequest]: loadResourceGroupInfoRequest,
  [A.loadResourceGroupInfoSuccess]: loadResourceGroupInfoSuccess,
  [A.loadResourceGroupInfoFailure]: loadResourceGroupInfoFailure,
  [A.loadAssignedResourcesRequest]: loadAssignedResourcesRequest,
  [A.loadAssignedResourcesSuccess]: loadAssignedResourcesSuccess,
  [A.loadAssignedResourcesFailure]: loadAssignedResourcesFailure,
  [A.cancelUnassignedLoadAssignment]: cancelUnassignedLoadAssignment,
}, getInitialState());
